/**
 * Copyright 2024 Sourcepole AG
 * Copyright 2021 TOPO graphics
 * 2024 Stadt Wolfsburg
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from 'react';
import {connect} from 'react-redux';

import PropTypes from 'prop-types';
import {setCurrentTask} from 'qwc2/actions/task';
import ResizeableWindow from 'qwc2/components/ResizeableWindow';

/**
 * Displays the ALKISBuch as an iframe in a floating dialog.
 */
class ALKISBuch extends React.Component {
    static propTypes = {
        active: PropTypes.bool,
        /** Default window geometry with size, position and docking status. Positive position values (including '0') are related to top (InitialY) and left (InitialX), negative values (including '-0') to bottom (InitialY) and right (InitialX). */
        geometry: PropTypes.shape({
            initialWidth: PropTypes.number,
            initialHeight: PropTypes.number,
            initialX: PropTypes.number,
            initialY: PropTypes.number,
            initiallyDocked: PropTypes.bool,
            side: PropTypes.string
        }),
        icon: PropTypes.string,
        iframe: PropTypes.object,
        origin: PropTypes.string,
        setCurrentTask: PropTypes.func,
        task: PropTypes.string,
        title: PropTypes.string
    };
    static defaultProps = {
        geometry: {
            initialWidth: 310,
            initialHeight: 630,
            initialX: 0,
            initialY: 0,
            initiallyDocked: false,
            side: 'left'
        },
        icon: 'alkis-buch',
        iframe: {
            path: '/ALKISBuch/client/index.html',
            style: {
                border: 'none',
                height: '600px',
                width: '310px'
            }
        },
        /** Protokoll, Host und Port, von wo aus ALKISBuch ausgeliefert wird */
        origin: '*',
        title: 'ALKISBuch'
    };
    state = {
        visible: false
    };
    constructor(props) {
        super(props);
        this.pong();
        window.addEventListener('message', (evt) => {
            const iframeElement = document.querySelector('#ALKISBuch iframe');
            let args;
            let layer;
            function gotGeometries(result, srs) {
                if (iframeElement) {
                    iframeElement.contentWindow.postMessage({
                        channel: 'alkisbuch',
                        msg: 'qwc2-gotGeometries',
                        result: result,
                        srs: srs
                    });
                }
            }
            if (evt.origin !== this.props.origin && this.props.origin !== '*') {
                return;
            }
            if (evt.data.channel === 'alkisbuch') {
                switch (evt.data.msg) {
                case 'qwc2-addFeature':
                    if (evt.data.args) {
                        if ('feature' in evt.data.args) {
                            layer = {
                                id: 'ALKISSelectLayer',
                                role: window.qwc2.LayerRole.SELECTION
                            };
                            window.qwc2.addLayerFeatures(layer, [evt.data.args.feature]);
                        }
                    }
                    break;
                case 'qwc2-clear':
                    window.parent.qwc2.removeLayer('ALKISSelectLayer');
                    break;
                case 'qwc2-drawScratch':
                    if (evt.data.args) {
                        args = evt.data.args;
                        if ('geometryType' in args && 'message' in args && 'useMultiSelect' in args && 'style' in args) {
                            window.qwc2.drawGeometry(args.geometryType, args.message, gotGeometries, {drawMultiple: args.useMultiSelect, style: args.style});
                        }
                    }
                    break;
                case 'qwc2-panTo':
                    if (evt.data.args) {
                        args = evt.data.args;
                        if ('center' in args && 'srs' in args) {
                            window.parent.qwc2.panTo(args.center, args.srs);
                        }
                    }
                    break;
                case 'qwc2-ping':
                    if (iframeElement) {
                        iframeElement.contentWindow.postMessage({
                            channel: 'alkisbuch',
                            msg: 'qwc2-pong'
                        });
                    }
                    break;
                case 'qwc2-zoomTo':
                    if (evt.data.args) {
                        args = evt.data.args;
                        if ('extent' in args && 'srs' in args) {
                            window.parent.qwc2.zoomToExtent(args.extent, args.srs);
                        }
                    }
                    break;
                default:
                    break;
                }
            }
        });
    }
    componentDidUpdate(prevProps) {
        if (this.props.active && !prevProps.active) {
            this.pong();
            this.setState({visible: true});
            // Clear task immediately, visibility is stored as state
            this.props.setCurrentTask(null);
            let elem = document.querySelector('#ALKISBuch .resizeable-window-body');
            let scrollBarWidth;
            let iframeElem;
            if (elem) {
                elem.style.margin = '0';
            }
            elem = document.querySelector('#ALKISBuch > .resizeable-window-container');
            if (elem) {
                elem.style.display = '';
                elem = document.querySelector('#ALKISBuch [role="body"]');
                if (elem && elem.scrollWidth > elem.offsetWidth) {
                    scrollBarWidth = elem.scrollWidth - elem.offsetWidth;
                    elem = document.querySelector('#ALKISBuch .resizeable-window');
                    iframeElem = document.querySelector('#ALKISBuch iframe');
                    if (elem && iframeElem) {
                        elem.style.width = (iframeElem.scrollWidth + scrollBarWidth) + 'px';
                    }
                }
            }
        }
    }
    pong() {
        const elem = document.querySelector('#ALKISBuch IFRAME');
        if (elem) {
            elem.contentWindow.postMessage({
                channel: 'alkisbuch',
                msg: 'qwc2-pong'
            }, "*");
        }
    }
    render() {
        if (!this.state.visible) {
            return null;
        }
        let location = window.location.search;
        let profile = 'profil=qwc2';
        this.pong();
        if (location.length > 1) {
            location = location.substring(1, location.length);
            location.split('&').forEach(function(part) {
                const kv = part.split('=');
                if (kv[0] === 'profil') {
                    profile = part;
                }
            });
        }
        return (
            <div id="ALKISBuch">
                <ResizeableWindow dockable={this.props.geometry.side} icon={this.props.icon}
                    initialHeight={this.props.geometry.initialHeight} initialWidth={this.props.geometry.initialWidth}
                    initialX={this.props.geometry.initialX} initialY={this.props.geometry.initialY}
                    initiallyDocked={this.props.geometry.initiallyDocked}
                    maxHeight={this.props.geometry.initialHeight} maxWidth={this.props.geometry.initialWidth} maximizeable={false}
                    onClose={this.onClose} scrollable={false} title={this.props.title}
                >
                    <div role="body">
                        <iframe src={this.props.iframe.path + '?' + profile} style={this.props.iframe.style} />
                    </div>
                </ResizeableWindow>
            </div>
        );
    }
    onClose = () => {
        this.setState({visible: false});
    };
}
export default connect(state => ({
    active: state.task.id === "ALKISBuch"
}), {
    setCurrentTask: setCurrentTask
})(ALKISBuch);
